import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Assistant from "../../screens/admin/assistant/assistant";

// Floating "open AI" launcher, mounted once globally by AppShell so every
// admin page has it. Click → opens the existing Assistant component inside
// an overlay panel. We hide on /admin/assistant so the FAB doesn't sit on
// top of the same UI when a user navigates there directly.

const overlayVariant = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};
const panelVariant = {
  hidden: { opacity: 0, scale: 0.97, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 10, transition: { duration: 0.15 } },
};

export default function AssistantFab() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Assistant mutates document.title on mount; save the host page's title
  // so we can restore it when the user closes the chat.
  const prevTitleRef = useRef(null);

  const hidden = location.pathname === "/admin/assistant";

  const openChat = () => {
    prevTitleRef.current = document.title;
    setOpen(true);
  };
  const closeChat = () => {
    if (prevTitleRef.current) {
      document.title = prevTitleRef.current;
      prevTitleRef.current = null;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeChat();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          onClick={openChat}
          aria-label="Open AI assistant"
          title="Ample Hire AI"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 56,
            height: 56,
            borderRadius: 28,
            background: "linear-gradient(135deg, #7B5AC4, #6A4DB3)",
            color: "white",
            border: "none",
            cursor: "pointer",
            boxShadow: "0 12px 28px rgba(79,70,229,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 60,
          }}
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* antenna */}
            <path d="M12 3v5" />
            <circle cx="12" cy="3" r="1" />
            {/* head */}
            <rect x="4" y="8" width="16" height="12" rx="2.5" />
            {/* side antennas */}
            <path d="M2 14h2" />
            <path d="M20 14h2" />
            {/* eyes */}
            <path d="M9 13v2" />
            <path d="M15 13v2" />
          </svg>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            variants={overlayVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-overlay"
            onClick={closeChat}
          >
            <motion.div
              variants={panelVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                width: "90vw",
                maxWidth: 1080,
                height: "88vh",
                background: "white",
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <button
                onClick={closeChat}
                aria-label="Close assistant"
                style={{
                  position: "absolute",
                  top: 14,
                  right: 16,
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  background: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  color: "white",
                  fontSize: 18,
                  lineHeight: 1,
                  cursor: "pointer",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                &times;
              </button>
              <div style={{ flex: 1, minHeight: 0 }}>
                <Assistant embedded />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
